'use client'

import React from 'react'
import { motion } from 'framer-motion'

const EASE: [number, number, number, number] = [0.2, 0.8, 0.2, 1]

const paragraphs = [
    'A completely new and profound element for me was the intensive focus on team dynamics while simultaneously understanding the influence of my own emotions within the team. Becoming aware of my emotional responses was the first essential step in interpreting situations more accurately.',
    'Instead of reacting from an automatic "functional mode," I am now able to take a step back and first become consciously aware of what is happening—both within myself and in the situation.',
    'This awareness creates the necessary space to consider different perspectives and then respond in a strategic, deliberate, and context‑appropriate way.'
]

export default function TestimonialSection() {
    return (
        <section className='relative overflow-hidden bg-page text-text'>
            {/* Ambient glow */}
            <div aria-hidden='true' className='pointer-events-none absolute inset-0'>
                <div className='absolute left-1/2 top-0 h-[700px] w-[1000px] -translate-x-1/2 rounded-full bg-[color-mix(in_oklab,var(--color-accent)_7%,transparent)] blur-[140px]' />
                <div className='absolute -right-48 bottom-0 h-[500px] w-[700px] rounded-full bg-[color-mix(in_oklab,var(--color-accent-2)_5%,transparent)] blur-[120px]' />
            </div>

            <div className='relative section-pad'>
                <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-12'>
                    {/* Section header */}
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.65, ease: EASE }}
                        className='max-w-208'>
                        <p className='text-[11px] font-semibold tracking-[0.28em] text-muted'>REAL-WORLD IMPACT</p>
                        <h2 className='mt-3 text-balance text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl'>
                            How does leadership coaching translate into
                            <span className='text-[color-mix(in_oklab,var(--color-accent)_85%,var(--color-text))]'>
                                {' '}
                                real&#8209;world impact?
                            </span>
                        </h2>
                        <p className='mt-4 text-pretty text-muted sm:text-lg'>
                            What clients say about the shift they experienced.
                        </p>
                    </motion.div>

                    {/* Testimonial card */}
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.1 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
                        className='relative mt-10 sm:mt-14'>
                        <div className='surface-strong relative overflow-hidden p-6 sm:p-10 lg:p-14'>
                            {/* Accent edge */}
                            <div
                                aria-hidden='true'
                                className='absolute inset-y-0 left-0 w-1 bg-[linear-gradient(to_bottom,transparent,var(--color-accent),var(--color-accent-2),transparent)] sm:w-1.5'
                            />

                            <div className='relative grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end lg:gap-16'>
                                {/* Quote content */}
                                <div>
                                    {/* Decorative quote */}
                                    <svg
                                        aria-hidden='true'
                                        viewBox='0 0 48 36'
                                        className='mb-5 h-7 w-9 text-[color-mix(in_oklab,var(--color-accent)_30%,transparent)] sm:mb-6 sm:h-9 sm:w-11'>
                                        <path
                                            fill='currentColor'
                                            d='M0 36V20.4C0 14 1.4 9 4.2 5.4 7 1.8 11.4 0 17.4 0l1.2 6.6c-3.2.4-5.6 1.6-7.2 3.6C9.8 12.2 9 14.6 9 17.4h8.4V36H0Zm26.4 0V20.4c0-6.4 1.4-11.4 4.2-15C33.4 1.8 37.8 0 43.8 0L45 6.6c-3.2.4-5.6 1.6-7.2 3.6-1.6 2-2.4 4.4-2.4 7.2H44V36H26.4Z'
                                        />
                                    </svg>

                                    <blockquote className='space-y-4 sm:space-y-5'>
                                        {paragraphs.map((p, i) => (
                                            <motion.p
                                                key={i}
                                                initial={{ opacity: 0, y: 6 }}
                                                whileInView={{ opacity: 1, y: 0 }}
                                                viewport={{ once: true, amount: 0.3 }}
                                                transition={{
                                                    duration: 0.55,
                                                    ease: EASE,
                                                    delay: 0.2 + i * 0.1
                                                }}
                                                className={`text-pretty leading-relaxed ${
                                                    i === paragraphs.length - 1
                                                        ? 'text-base font-medium text-text sm:text-lg lg:text-xl'
                                                        : 'text-sm text-muted sm:text-base lg:text-lg'
                                                }`}>
                                                {p}
                                            </motion.p>
                                        ))}
                                    </blockquote>
                                </div>

                                {/* Attribution */}
                                <motion.footer
                                    initial={{ opacity: 0, y: 8 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.5 }}
                                    transition={{ duration: 0.5, ease: EASE, delay: 0.5 }}
                                    className='flex items-center gap-4 border-t border-[color-mix(in_oklab,var(--color-accent)_12%,transparent)] pt-6 lg:flex-col lg:items-start lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0'>
                                    {/* Avatar placeholder */}
                                    <div className='flex h-12 w-12 flex-none items-center justify-center rounded-full bg-[color-mix(in_oklab,var(--color-accent)_16%,transparent)] text-lg font-bold text-[color-mix(in_oklab,var(--color-accent)_80%,var(--color-text))] sm:h-14 sm:w-14'>
                                        DN
                                    </div>
                                    <div>
                                        <p className='text-base font-semibold tracking-tight sm:text-lg'>
                                            Dr.&nbsp;Neurieder
                                        </p>
                                        <p className='mt-0.5 text-sm text-muted'>Team Leader Veterinary Consultants</p>
                                        <p className='mt-0.5 text-xs font-medium tracking-wide text-[color-mix(in_oklab,var(--color-accent)_65%,var(--color-muted))]'>
                                            Antech Lab Germany
                                        </p>
                                    </div>
                                </motion.footer>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    )
}
